import React, { lazy, Suspense, useEffect, useState } from 'react';
import {
  HashRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import './App.css';
import { DeviceProvider, useDevice } from './context/DeviceContext';
import Sidebar from './components/Sidebar';
import ConnectDevice from './components/ConnectDevice';
import ChevronIcon from './components/ChevronIcon';
import DeviceDebugger from './components/DeviceDebugger';
import Settings from './components/Settings';
import KeyFunction from './components/KeyFunction';
import MacroManagement from './components/MacroManagement';
import AdvancedSettings from './components/AdvancedSettings';
import ToastContainer, { toast } from './components/Toast';
import DeviceSleepOverlay from './components/DeviceSleepOverlay';
import ProfileDropdown from './components/ProfileDropdown';
import Sensitivity from './components/Sensitivity';
import FirmwareBinTester from './components/FirmwareBinTester';
import Theme2Navigation from './components/Theme2Navigation';
import {
  BACKGROUND_PRESETS,
  getBackgroundPreset,
} from './assets/backgrounds';
import { formatConnectionLabel } from './utils/DeviceCatalog';
import {
  readBackgroundPreference,
  writeBackgroundPreference,
} from './utils/backgroundPreferences';
import {
  readThemePreference,
  writeThemePreference,
} from './utils/themePreferences';
import { useVirtualKeysSupport } from './utils/useVirtualKeysSupport';
import {
  RT_LAB_TTL_LABEL,
  copyRtLabShare,
  isRtLabPathname,
  issueRtLabShare,
  readRtLabPathname,
} from './utils/hiddenRtLabRoute';

const WheelSettings = lazy(() => import('./components/WheelSettings'));
const ButtonPerformanceSettings = lazy(() => import('./components/HitsSettings'));
const LightingSettings = lazy(() => import('./components/LightingSettings'));

function RtLabPage() {
  const { token } = useParams();
  const status = readRtLabPathname(`/p/${token || ''}`);
  useEffect(() => {
    if (status.expired) {
      toast.error('临时访问已过期，请重新索取链接');
    }
  }, [status.expired]);
  if (!status.valid) {
    return <Navigate to="/sensitivity" replace />;
  }
  return <ButtonPerformanceSettings view="lab" />;
}

function FirmwareUpgradeCompletionDialog({ completion, onClose }) {
  if (!completion) return null;

  return (
    <div className="firmware-dialog-backdrop">
      <section
        className="firmware-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="firmware-completion-title"
      >
        <div className="firmware-dialog-header">
          <div>
            <span>设备升级</span>
            <h2 id="firmware-completion-title">版本更新成功</h2>
          </div>
          <button
            type="button"
            className="firmware-dialog-close"
            onClick={onClose}
            aria-label="关闭升级成功弹窗"
          >
            ×
          </button>
        </div>

        <div className="firmware-handshake-success firmware-completion-message">
          <strong>固件已全部写入并通过最终校验</strong>
          <span>设备已重启，请在设备列表中重新连接设备。</span>
        </div>

        <div className="firmware-progress-list">
          <div className="firmware-progress">
            <div className="firmware-progress-meta">
              <span>升级进度</span>
              <strong>100%</strong>
            </div>
            <div
              className="firmware-progress-track"
              role="progressbar"
              aria-label="设备固件升级进度"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow="100"
            >
              <span style={{ width: '100%' }} />
            </div>
          </div>
        </div>

        <div className="firmware-dialog-actions">
          <button type="button" className="firmware-upgrade-button" onClick={onClose}>
            返回设备选择
          </button>
        </div>
      </section>
    </div>
  );
}

function AppContent() {
  const [selectedProfile, setSelectedProfile] = useState(1);
  const [switchingProfile, setSwitchingProfile] = useState(null);
  const [showDebugger, setShowDebugger] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [batteryInfo, setBatteryInfo] = useState(null);
  const [firmwareUpgradeCompletion, setFirmwareUpgradeCompletion] = useState(null);
  const [backgroundPreference, setBackgroundPreference] = useState(
    readBackgroundPreference
  );
  const [uiTheme, setUiTheme] = useState(readThemePreference);
  const {
    isConnected,
    driverReady,
    connectionType,
    sender,
    dataStore,
    deviceInfo,
    activeAdapter,
    disconnectDevice,
  } = useDevice();
  const location = useLocation();
  const navigate = useNavigate();
  const isDeviceSelectionPath = location.pathname === '/';
  const {
    ready: virtualKeysReady,
    supported: virtualKeysSupported,
  } = useVirtualKeysSupport();

  useEffect(() => {
    if (!driverReady && !isDeviceSelectionPath) {
      navigate('/', { replace: true });
    }
  }, [driverReady, isDeviceSelectionPath, navigate]);

  const handleSwitchDevice = () => {
    if (disconnectDevice) disconnectDevice();
    navigate('/');
  };

  // 同步设备当前配置文件索引到顶栏下拉
  useEffect(() => {
    if (!dataStore) return;
    const sync = (data) => {
      if (data?.parsed?.profileIndex !== undefined) {
        // 板载配置 1–4；超出时仍显示设备回报值，由下拉兼容处理
        const n = Number(data.parsed.profileIndex) + 1;
        setSelectedProfile(Math.max(1, Math.min(5, n)));
      }

      const batteryLevel = Number(data?.parsed?.batteryLevel);
      if (Number.isFinite(batteryLevel)) {
        setBatteryInfo({
          level: Math.max(0, Math.min(100, batteryLevel)),
          charging: Number(data?.parsed?.chargingFlag) === 1,
        });
      }
    };
    const info = dataStore.getData('informationData');
    if (info) sync(info);
    dataStore.on('informationData', sync);
    return () => dataStore.off('informationData', sync);
  }, [dataStore]);

  useEffect(() => {
    if (!isConnected) setBatteryInfo(null);
  }, [isConnected]);

  // 统一的配置文件切换方法
  const handleProfileChange = async (profileNum) => {
    if (switchingProfile !== null) return;
    if (!sender || !isConnected || !dataStore) {
      toast.warning('设备未连接，无法切换配置文件');
      return;
    }

    const targetIndex = profileNum - 1;
    setSwitchingProfile(profileNum);

    const requestInformationData = () =>
      new Promise((resolve, reject) => {
        let settled = false;
        const finish = (callback, value) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          dataStore.off('informationData', onInformation);
          callback(value);
        };
        const onInformation = (data) => finish(resolve, data);
        const timer = window.setTimeout(
          () => finish(reject, new Error('设备响应超时')),
          600
        );

        dataStore.on('informationData', onInformation);
        sender.getInformationData().catch((error) => finish(reject, error));
      });

    try {
      console.log(`切换配置文件到: ${profileNum} (设备索引: ${targetIndex})`);
      await sender.changeProfile(targetIndex);
      await sender.wait(100);

      let confirmed = false;
      let lastError = null;
      for (let attempt = 0; attempt < 3 && !confirmed; attempt += 1) {
        try {
          const information = await requestInformationData();
          confirmed = Number(information?.parsed?.profileIndex) === targetIndex;
        } catch (error) {
          lastError = error;
        }
        if (!confirmed && attempt < 2) await sender.wait(120);
      }

      if (!confirmed) {
        throw lastError || new Error('设备未确认新的配置文件');
      }

      setSelectedProfile(profileNum);

      try {
        await sender.getMouseButtonData();
        await sender.wait(50);
        await sender.getPerformanceData(1);
        await sender.wait(50);
        await sender.getPerformanceData(3);
        toast.success(`已切换到板载配置${profileNum}`);
      } catch (refreshError) {
        console.error('配置文件已切换，但刷新配置数据失败:', refreshError);
        toast.warning(`已切换到板载配置${profileNum}，部分数据刷新失败`);
      }
    } catch (error) {
      console.error('切换配置文件失败:', error);
      toast.error(`切换配置文件失败：${error.message || '设备无响应'}`);
    } finally {
      setSwitchingProfile(null);
    }
  };

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      setShowDebugger(true);
    }
  }, []);

  React.useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        setShowDebugger((prev) => !prev);
        return;
      }
      // 行程校准：签发 24 小时临时链接并复制，对方打开即可，不用快捷键
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'l') {
        e.preventDefault();
        copyRtLabShare()
          .then((share) => {
            navigate(share.path);
            toast.success(`临时访问链接已复制，${RT_LAB_TTL_LABEL}内有效`);
          })
          .catch(() => {
            const share = issueRtLabShare();
            navigate(share.path);
            toast.info(`请复制地址栏链接，${RT_LAB_TTL_LABEL}内有效`);
          });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate]);

  const rawPath = location.pathname.replace(/^\//, '');
  // 兼容旧路由 profile/dpi → 灵敏度
  const currentPath =
    !rawPath || rawPath === 'profile' || rawPath === 'dpi' || rawPath === 'sensitivity'
      ? 'sensitivity'
      : rawPath;
  useEffect(() => {
    if (
      (currentPath === 'virtual-keys' || currentPath === 'calibration') &&
      virtualKeysReady &&
      !virtualKeysSupported
    ) {
      navigate('/hits', { replace: true });
    }
    if (
      isRtLabPathname(location.pathname) &&
      virtualKeysReady &&
      !virtualKeysSupported
    ) {
      navigate('/hits', { replace: true });
    }
  }, [
    currentPath,
    location.pathname,
    virtualKeysReady,
    virtualKeysSupported,
    navigate,
  ]);
  const batteryStatus = !batteryInfo
    ? '状态未知'
    : batteryInfo.charging
      ? '充电中'
      : batteryInfo.level >= 100
        ? '已充满'
        : '未充电';
  const batteryStatusClass = batteryInfo?.charging
    ? 'charging'
    : batteryInfo?.level >= 100
      ? 'full'
      : 'not-charging';
  const supportsWheelSettings =
    activeAdapter?.capabilities?.wheelSettings === true;
  const supportsHitsSettings =
    activeAdapter?.capabilities?.hitsSettings === true;
  const supportsLightingSettings =
    activeAdapter?.capabilities?.lightingSettings === true;

  const handleBackgroundChange = (nextPreference) => {
    setBackgroundPreference(writeBackgroundPreference(nextPreference));
  };

  const handleThemeChange = (nextTheme) => {
    setUiTheme(writeThemePreference(nextTheme));
  };

  const selectedBackground = getBackgroundPreset(
    backgroundPreference.preset
  ).image;
  // homepage="./" 会产出 ./static/media/...。相对 URL 若直接放进 CSS
  // 变量，会被外部样式表按 static/css/ 解析，形成错误的
  // static/css/static/media/... 路径，因此先按当前页面地址转换为绝对 URL。
  const selectedBackgroundUrl = new URL(
    selectedBackground,
    window.location.href
  ).href;
  const appBackgroundStyle = {
    '--app-solid-background': backgroundPreference.color,
    '--app-preset-background': `url("${selectedBackgroundUrl}")`,
  };
  const appClassName = `app app-background-${backgroundPreference.type} app-${uiTheme}`;

  // 根路径始终是设备选择页。连接成功后会进入独立的灵敏度路径，
  // 因而浏览器“返回”可以自然回到设备列表。
  if (isDeviceSelectionPath || !driverReady) {
    return (
      <div className={`${appClassName} app-connection-only`} style={appBackgroundStyle}>
        <ConnectDevice />
        <FirmwareUpgradeCompletionDialog
          completion={firmwareUpgradeCompletion}
          onClose={() => setFirmwareUpgradeCompletion(null)}
        />
        <ToastContainer />
      </div>
    );
  }

  return (
    <div
      className={`${appClassName} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}
      style={appBackgroundStyle}
    >
      {uiTheme === 'theme1' && (
        <Sidebar
          currentPath={currentPath}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
        />
      )}
      <div className="app-body">
        <header className={`app-topbar ${uiTheme === 'theme2' ? 'theme2-topbar' : ''}`}>
          {uiTheme === 'theme2' && (
            <button type="button" className="theme2-home-pill" onClick={handleSwitchDevice}>
              <span className="theme2-home-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1v-9.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                </svg>
              </span>
              主页
            </button>
          )}
          {uiTheme === 'theme2' ? (
            <ProfileDropdown
              selectedProfile={selectedProfile}
              handleProfileChange={handleProfileChange}
              switchingProfile={switchingProfile}
              variant="theme2"
            />
          ) : (
            <div
              className="app-topbar-device"
              title={
                isConnected
                  ? deviceInfo?.productName || '已连接设备'
                  : '未连接设备'
              }
            >
              <span className="app-topbar-device-name">
                {isConnected
                  ? deviceInfo?.productName || '已连接设备'
                  : '未连接设备'}
              </span>
              {connectionType && (
                <span className={`app-connection-badge ${connectionType}`}>
                  {formatConnectionLabel(connectionType)}
                </span>
              )}
              {isConnected && (
                <span
                  className={`app-battery-badge ${batteryStatusClass}`}
                  title={
                    batteryInfo
                      ? `${batteryStatus}，电量 ${batteryInfo.level}%`
                      : '等待设备上报电池状态'
                  }
                >
                  <span className="app-battery-icon" aria-hidden="true">
                    <span
                      className="app-battery-icon-level"
                      style={{ width: batteryInfo ? `${batteryInfo.level}%` : '0%' }}
                    />
                  </span>
                  <span className="app-battery-status-text">
                    {batteryStatus}
                  </span>
                  <span className="app-battery-level">
                    {batteryInfo ? `${batteryInfo.level}%` : '--'}
                  </span>
                </span>
              )}
            </div>
          )}
          {uiTheme === 'theme2' && (
            <div
              className="app-topbar-device theme2-device-summary"
              title={
                batteryInfo
                  ? `${deviceInfo?.productName || '已连接设备'}，${batteryStatus}，电量 ${batteryInfo.level}%`
                  : `${deviceInfo?.productName || '已连接设备'}，等待设备上报电池状态`
              }
            >
              <span className="app-topbar-device-name">
                {deviceInfo?.productName || '已连接设备'}
              </span>
              <span
                className={`app-battery-badge ${batteryStatusClass}`}
                aria-label={
                  batteryInfo
                    ? `${batteryStatus}，电量 ${batteryInfo.level}%`
                    : '电池状态未知'
                }
              >
                <span className="app-battery-icon" aria-hidden="true">
                  <span
                    className="app-battery-icon-level"
                    style={{ width: batteryInfo ? `${batteryInfo.level}%` : '0%' }}
                  />
                </span>
                <span className="app-battery-status-text">{batteryStatus}</span>
                <span className="app-battery-level">
                  {batteryInfo ? `${batteryInfo.level}%` : '--'}
                </span>
              </span>
            </div>
          )}
          <div className="app-topbar-spacer" />
          {isConnected && uiTheme === 'theme1' && (
            <button
              type="button"
              className="app-topbar-back-devices"
              onClick={handleSwitchDevice}
              title="断开当前设备并返回设备选择界面"
            >
              <span className="app-topbar-back-icon" aria-hidden="true">
                <ChevronIcon direction="left" />
              </span>
              <span className="app-topbar-back-text">返回设备列表</span>
            </button>
          )}
          {uiTheme === 'theme1' ? (
            <ProfileDropdown
              selectedProfile={selectedProfile}
              handleProfileChange={handleProfileChange}
              switchingProfile={switchingProfile}
            />
          ) : (
            <div className="theme2-topbar-actions">
              <button
                type="button"
                className={`theme2-round-action theme2-settings-action ${currentPath === 'settings' ? 'active' : ''}`}
                onClick={() => navigate('/settings')}
                aria-label="设置"
                title="设置"
              >
                <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z" stroke="currentColor" strokeWidth="1.7" />
                  <path d="M19.4 13.5a7.8 7.8 0 0 0 .05-3l1.65-1.28-2-3.45-2.05.82a8 8 0 0 0-2.6-1.5L14.15 3h-4.3l-.3 2.1a8 8 0 0 0-2.6 1.5L4.9 5.77l-2 3.45 1.65 1.28a7.8 7.8 0 0 0 .05 3l-1.7 1.32 2 3.45 2.12-.85a8 8 0 0 0 2.53 1.46L9.85 21h4.3l.3-2.12a8 8 0 0 0 2.53-1.46l2.12.85 2-3.45-1.7-1.32Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          )}
        </header>
        <div className="app-content">
          <Suspense fallback={null}>
            <Routes>
              <Route path="/sensitivity" element={<Sensitivity theme={uiTheme} />} />
              <Route path="/profile" element={<Sensitivity theme={uiTheme} />} />
              <Route path="/dpi" element={<Sensitivity theme={uiTheme} />} />
              <Route path="/keys" element={<KeyFunction />} />
              <Route path="/macros" element={<MacroManagement />} />
              <Route path="/advanced" element={<AdvancedSettings />} />
              {supportsWheelSettings && (
                <Route path="/wheel" element={<WheelSettings />} />
              )}
              <Route path="/hits" element={<ButtonPerformanceSettings />} />
              <Route
                path="/virtual-keys"
                element={<ButtonPerformanceSettings view="virtual" />}
              />
              <Route
                path="/calibration"
                element={<ButtonPerformanceSettings view="lab" />}
              />
              <Route path="/p/:token" element={<RtLabPage />} />
              {supportsLightingSettings && (
                <Route path="/lighting" element={<LightingSettings />} />
              )}
              <Route
                path="/firmware-update"
                element={
                  <FirmwareBinTester
                    onUpgradeStart={() => setFirmwareUpgradeCompletion(null)}
                    onUpgradeSuccess={setFirmwareUpgradeCompletion}
                    onUpgradeSuccessDismiss={() => setFirmwareUpgradeCompletion(null)}
                  />
                }
              />
              <Route
                path="/settings"
                element={
                  <Settings
                    backgroundPreference={backgroundPreference}
                    onBackgroundChange={handleBackgroundChange}
                    backgroundPresets={BACKGROUND_PRESETS}
                    uiTheme={uiTheme}
                    onThemeChange={handleThemeChange}
                  />
                }
              />
              <Route path="*" element={<Navigate to="/sensitivity" replace />} />
            </Routes>
          </Suspense>
        </div>
      </div>
      {uiTheme === 'theme2' && <Theme2Navigation currentPath={currentPath} />}
      {showDebugger && <DeviceDebugger onClose={() => setShowDebugger(false)} />}
      {/* 无线设备休眠/失联时覆盖整个驱动界面，不再自动退回设备选择页 */}
      <DeviceSleepOverlay />
      <ToastContainer />
    </div>
  );
}

function App() {
  return (
    <HashRouter>
      <DeviceProvider>
        <AppContent />
      </DeviceProvider>
    </HashRouter>
  );
}

export default App;
